// Master-Sprint P0 — header Alert-Bell + Alert Center panel.
// Groups the existing alert events (saved-search new matches +
// price-change intents) — read-only on the email alert flow.
//
// 2026-08 P0 fix — price change direction: the item's `direction`
// field from the API is the source of truth. When missing we
// defensively recompute from open/close so historical intents
// (which the worker used to always label "drop") render correctly.
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bell, TrendingDown, TrendingUp, Sparkles } from "lucide-react";
import { fetchAlertCenter, markAlertCenterSeen } from "../../api/alertCenterApi";
import { useAuth } from "../../auth/AuthContext";

const fmtEur = (v) =>
  typeof v === "number" ? `€ ${Math.round(v).toLocaleString("de-AT")}` : "—";

// Defensive: recompute direction + signed pct from open/close when
// available. Legacy `drop_pct` and older `direction` fields are
// NEVER trusted when raw prices are present — earlier worker builds
// wrote drop_pct for raises with a negative sign, so if we blindly
// respect the server's `direction` we'd render raises as drops.
// See `priceChangeSummary.js` for the shared implementation & tests.
import { priceChangeSummary } from "./priceChangeSummary";

export default function AlertBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const { data } = useQuery({
    queryKey: ["alert-center"],
    queryFn: fetchAlertCenter,
    // Don't fire the protected /api/alert-center call for a still-unverified
    // user (e.g. right after registration) — it 403s (EMAIL_NOT_VERIFIED)
    // and would trigger the global verify redirect.
    enabled: user?.email_verified !== false,
    refetchInterval: 120000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const unseen = data?.unseen_count || 0;
  const matches = data?.new_matches || [];
  const drops = data?.price_drops || [];
  const hasUps = Boolean(data?.has_price_ups);
  const sectionTitleKey = hasUps
    ? "alertCenter.priceChanges"
    : "alertCenter.priceDrops";
  const sectionTitleFallback = hasUps ? "Preisänderungen" : "Preissenkungen";
  const empty = matches.length === 0 && drops.length === 0;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unseen > 0) {
      markAlertCenterSeen()
        .then(() => {
          qc.setQueryData(["alert-center"], (old) =>
            old ? { ...old, unseen_count: 0 } : old,
          );
        })
        .catch(() => {});
    }
  };

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="alert-bell-button"
        aria-label={t("alertCenter.bellLabel")}
        onClick={toggle}
        className={`relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
          open
            ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
            : "border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
        }`}
      >
        <Bell className="h-4 w-4" />
        {unseen > 0 && (
          <span
            data-testid="alert-bell-badge"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold leading-none text-zinc-950"
          >
            {unseen > 9 ? "9+" : unseen}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="alert-center-panel"
          className="fixed inset-x-2 top-14 z-[1300] max-h-[70vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-3 shadow-2xl shadow-black/60 sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:w-96"
        >
          <div className="flex items-center justify-between pb-2">
            <span className="text-sm font-semibold text-zinc-100">
              {t("alertCenter.title")}
            </span>
            <Link
              to="/alerts"
              data-testid="alert-center-manage-link"
              onClick={() => setOpen(false)}
              className="text-[11px] text-zinc-500 underline-offset-4 hover:text-amber-300 hover:underline"
            >
              {t("alertCenter.viewAlerts")}
            </Link>
          </div>

          {empty && (
            <div
              data-testid="alert-center-empty"
              className="rounded-xl border border-dashed border-zinc-800 px-4 py-6 text-center text-xs text-zinc-500"
            >
              {t("alertCenter.empty")}
            </div>
          )}

          {matches.length > 0 && (
            <div className="space-y-1.5 pb-2">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-400/80">
                <Sparkles className="h-3 w-3" />
                {t("alertCenter.newMatches")}
              </div>
              {matches.map((g) => (
                <button
                  key={g.alert_ulid}
                  type="button"
                  data-testid={`alert-center-match-${g.alert_ulid}`}
                  onClick={() => go("/alerts")}
                  className="block w-full rounded-xl border border-zinc-800/80 bg-zinc-900/60 px-3 py-2 text-left transition-colors hover:border-emerald-500/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-medium text-zinc-100">
                      {g.name}
                    </span>
                    <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
                      {t("alertCenter.matchCount", { count: g.count })}
                    </span>
                  </div>
                  {g.items?.[0]?.title && (
                    <div className="mt-0.5 truncate text-[11px] text-zinc-500">
                      {g.items[0].title}
                      {g.items[0].price != null ? ` · ${fmtEur(g.items[0].price)}` : ""}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {drops.length > 0 && (
            <div className="space-y-1.5">
              <div
                className={
                  "flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest " +
                  (hasUps ? "text-zinc-300/80" : "text-amber-400/80")
                }
              >
                {hasUps ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {t(sectionTitleKey, { defaultValue: sectionTitleFallback })}
              </div>
              {drops.map((d) => {
                const { direction, pct } = priceChangeSummary(d);
                const isUp = direction === "up";
                const isDown = direction === "down";
                const closeColor = isUp
                  ? "text-orange-300"
                  : isDown
                    ? "text-emerald-300"
                    : "text-zinc-300";
                const pillColor = isUp
                  ? "bg-orange-500/15 text-orange-300"
                  : "bg-emerald-500/15 text-emerald-300";
                const sign = isUp ? "+" : isDown ? "−" : "";
                const magnitude =
                  typeof pct === "number" ? Math.round(Math.abs(pct) * 100) : null;
                return (
                  <button
                    key={`${d.listing_ulid}-${d.created_at}`}
                    type="button"
                    data-testid={`alert-center-drop-${d.listing_ulid}`}
                    data-direction={direction || "unknown"}
                    onClick={() => go(`/listing/${d.listing_ulid}`)}
                    className={
                      "flex w-full items-center gap-2.5 rounded-xl border border-zinc-800/80 bg-zinc-900/60 px-3 py-2 text-left transition-colors " +
                      (isUp
                        ? "hover:border-orange-500/40"
                        : "hover:border-amber-500/40")
                    }
                  >
                    {d.primary_image_url ? (
                      <img
                        src={d.primary_image_url}
                        alt=""
                        loading="lazy"
                        className="h-9 w-12 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <div className="h-9 w-12 shrink-0 rounded-md bg-zinc-800" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className="truncate text-xs text-zinc-100">
                          {d.title || d.listing_ulid}
                        </div>
                        {/* P1.3 — vertical badge (Auto vs. Immo) so users
                            can distinguish drops at a glance. */}
                        {d.vertical && (
                          <span
                            data-testid={`alert-center-drop-vertical-${d.listing_ulid}`}
                            className={
                              "shrink-0 rounded px-1 py-[1px] text-[9px] font-bold uppercase tracking-wide " +
                              (d.vertical === "auto"
                                ? "bg-blue-500/15 text-blue-300 border border-blue-500/30"
                                : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30")
                            }
                          >
                            {t(`alertCenter.vertical.${d.vertical === "auto" ? "auto" : "immo"}`, {
                              defaultValue: d.vertical === "auto" ? "Auto" : "Immo",
                            })}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        <span className="line-through">{fmtEur(d.open_price)}</span>{" "}
                        <span className={`font-semibold ${closeColor}`}>
                          {fmtEur(d.close_price)}
                        </span>
                      </div>
                    </div>
                    {magnitude != null && (
                      <span
                        data-testid={`alert-center-drop-pct-${d.listing_ulid}`}
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${pillColor}`}
                      >
                        {sign}
                        {magnitude} %
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
